import { useState, useEffect, useCallback } from 'react';
import useActiveOrganisation from '../collaboration/useActiveOrganisation';

interface Instruction {
  content: string;
  timestamp: number;
}

export const useInstructions = () => {
  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeOrganisation] = useActiveOrganisation();

  const fetchInstructions = useCallback(async () => {
    if (!activeOrganisation?.organizationId) return;

    try {
      const response = await fetch('/api/db/business/metadata/accounting_instructions', {
        headers: {
          'X-Organization': activeOrganisation.organizationId,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch instructions');
      }

      const data = await response.json();
      if (data && data.value) {
        let instructionsArray: Instruction[];
        try {
          const parsedValue = JSON.parse(data.value);
          if (Array.isArray(parsedValue)) {
            instructionsArray = parsedValue.map(instruction => {
              if (typeof instruction === 'object' && instruction.content && instruction.timestamp) {
                return instruction as Instruction;
              }
              return {
                content: typeof instruction === 'string' ? instruction : String(instruction),
                timestamp: Date.now()
              };
            });
          } else {
            instructionsArray = [{
              content: String(data.value),
              timestamp: Date.now()
            }];
          }
        } catch (e) {
          instructionsArray = [{
            content: String(data.value),
            timestamp: Date.now()
          }];
        }
        setInstructions(instructionsArray);
      } else {
        setInstructions([]);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [activeOrganisation?.organizationId]);

  // Initial fetch
  useEffect(() => {
    setIsLoading(true);
    fetchInstructions();
  }, [fetchInstructions]);

  // Refetch function
  const refetch = useCallback(async () => {
    setIsRefetching(true);
    await fetchInstructions();
    setIsRefetching(false);
  }, [fetchInstructions]);

  return {
    instructions,
    error,
    refetch,
    isRefetching,
    isLoading,
  };
}; 